import React from 'react';
import { Separator, Stack } from '@fluentui/react';
import { Info16Regular } from '@fluentui/react-icons';
import { ActionButton } from 'common/components/Buttons/v8';
import { DialogCardError } from 'common/components/Cards/DialogCard/DialogCardError';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { Text } from 'common/components/Text';
import { useLocalizedStrings } from 'i18n';
import { isApiCallComplete } from 'store';
import { isApiCallFailed } from 'store/apiCallResultStatus';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';
import { useWebView } from 'store/webView/useWebView';

import { stackTokens, useNotificationSettingsStyles } from '../NotificationSettings.styles';
import { NotificationSettingsSummary } from '../NotificationSettingsSummary';

import { PushNotificationSettingsSummary } from './PushNotificationSettingsSummary';

export const PushNotificationSettings = () => {
  const { t } = useLocalizedStrings();
  const { notificationSettingsSummary, notificationSettingsSummaryLoadStatus } = useNotificationSettingsSummary();
  const { updateSelfNotificationTopicSettings } = useNotificationsDispatch();
  const { isMobileView } = usePanelMobileView();
  const { isWebView, openNativeNotificationSettings } = useWebView();
  const classNames = useNotificationSettingsStyles();

  const onToggleDelegate = React.useCallback(
    (topicId: string) => (checked?: boolean) => {
      updateSelfNotificationTopicSettings({ channel: 'Push', topicId, isEnabled: !!checked });
    },
    [updateSelfNotificationTopicSettings]
  );

  if (!isApiCallComplete(notificationSettingsSummaryLoadStatus)) {
    return <ModalShimmer />;
  }
  if (isApiCallFailed(notificationSettingsSummaryLoadStatus)) {
    return (
      <DialogCardError
        title={t('OfficeShellPanel.NotificationSettings.Error.Title')}
        subtitle={t('OfficeShellPanel.NotificationSettings.Error.Subtitle')}
      />
    );
  }
  return (
    <Stack tokens={isMobileView ? stackTokens.gapXXL : stackTokens.gapMD}>
      <PushNotificationSettingsSummary
        title={isMobileView ? t('OfficeShellPanel.NotificationSettings.Sections.Push.Title') : undefined}
        text={isMobileView ? t('OfficeShellPanel.NotificationSettings.Sections.Push.DrillDownText') : undefined}
        iconComponent={undefined}
      />
      {!isMobileView && <Separator className={classNames.noPadding} />}
      <Stack tokens={isMobileView ? undefined : stackTokens.gapSM}>
        {!isMobileView && <Text block>{t('OfficeShellPanel.NotificationSettings.Sections.Push.DrillDownText')}</Text>}
        {notificationSettingsSummary!.pushNotifications.map((topicSetting) => (
          <NotificationSettingsSummary
            key={topicSetting.topicId}
            className={classNames.notificationSetting}
            notificationType="Push"
            telemetryProps={{ topicId: topicSetting.topicId }}
            title={isMobileView ? topicSetting.topicName : undefined}
            toggleNotificationLabel={isMobileView ? '' : topicSetting.topicName}
            toggleAriaLabel={topicSetting.topicName}
            notificationsEnabled={topicSetting.isEnabled ?? false}
            onToggleNotification={onToggleDelegate(topicSetting.topicId)}
            disabledReason={topicSetting.disabledReason}
          />
        ))}
      </Stack>
      {isWebView && (
        <Stack horizontal tokens={stackTokens.gapXS} verticalAlign="center">
          <Stack.Item shrink={false}>
            <Info16Regular />
          </Stack.Item>
          <Text variant="Caption2Strong">
            {t('OfficeShellPanel.NotificationSettings.Sections.Push.NativeCaption')}{' '}
            <ActionButton onClick={openNativeNotificationSettings} className={classNames.inlineActionButton}>
              <Text variant="Caption2Strong" className={classNames.inlineActionButtonText}>
                {t('OfficeShellPanel.NotificationSettings.Sections.Native.OpenSettings')}
              </Text>
            </ActionButton>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
